import classNames from 'classnames'
import { FormEvent, MouseEvent, useContext, useState } from 'react'
import { useTranslation } from 'next-i18next'
import TagManager from 'react-gtm-module'
import { AppContext } from 'doctors/context/app'
import useFormie from 'doctors/hooks/use-formie'
import { AButton } from 'ui'
import SvgCheckTrueIcon from 'ui/svg/icons/fill/check-true.svg'
import SvgCheckFalseIcon from 'ui/svg/icons/fill/check-false.svg'
import { AFormInput } from 'doctors/components/atoms/a-form-input'
import { AFormTextarea } from 'doctors/components/atoms/a-form-textarea'
import { AFormSelect } from 'doctors/components/atoms/a-form-select'
import * as CONST from 'ui/constants/index'
import { getIsTestEmail, useSiteInfo } from 'shared'
import { ENABLE_PREFERRED_DAYS } from '../../config/constant'
import { CraftDoctor, CraftLocation } from 'doctors/craft-types'

type Props = {
  doctor?: CraftDoctor
  location?: CraftLocation
  onCancel: (event: MouseEvent<HTMLButtonElement>) => void
  onSuccess: (data: {
    lastName: string
    email: string
    phone: string
    message: string
    isSubscribed: boolean
  }) => void
}

const LOOKING_TO_DO_OPTIONS = [
  { label: 'Book an EVO ICL consultation', value: 'Book an EVO ICL consultation' },
  { label: 'Book a cataract consultation', value: 'Book a cataract consultation' },
  { label: 'Ask a question', value: 'Ask a question' },
]

const DAY_OPTIONS = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Any day', value: 'any-day' },
]

export const CContactFormFieldsV2 = ({
  doctor,
  location,
  onCancel,
  onSuccess,
}: Props) => {
  const appContext = useContext(AppContext)
  const formie = useFormie()
  const siteInfo = useSiteInfo()
  const { t } = useTranslation('common')

  const [errors, setErrors] = useState<any>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [postcode, setPostcode] = useState<string>('')
  const [whatAreYouLookingToDo, setWhatAreYouLookingToDo] = useState<string>(
    LOOKING_TO_DO_OPTIONS[0].value
  )
  const [message, setMessage] = useState<string>('')
  const [gdprAcceptance, setGdprAcceptance] = useState<boolean>(false)
  const [days, setDays] = useState<string[]>([])

  const SvgCheckIcon = gdprAcceptance ? SvgCheckTrueIcon : SvgCheckFalseIcon

  const submittedDays = ENABLE_PREFERRED_DAYS ? days : []
  const submittedWhatAreYouLookingToDo = siteInfo.isUK
    ? whatAreYouLookingToDo
    : ''

  const pushSegment = (event: string, data: any, identifyData?: any) => {
    if (!window.analytics || !siteInfo.isUS) {
      return
    }

    window.analytics.track(event, data)

    if (identifyData) {
      window.analytics.identify(data.email, identifyData)
    }
  }

  const handleDataLayer = () => {
    if (doctor?.id) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'contactFormSubmit',
          event_category: 'Contact Form Submissions',
          event_action: doctor.title,
          event_label: window.location.href,
        },
      })

      pushSegment(
        'Doctor Contact Submitted',
        {
          doctar_name: doctor.title,
          specialist_doctor: doctor.doctorSpecialist,
          doctor_clinic: doctor.doctorLocations?.[0]?.title,
          doctor_address: doctor.doctorLocations?.[0]?.locationAddress,
          review_type: doctor.doctorReviewType ? doctor.doctorReviewType : null,
          review_available: !!doctor.doctorReviewId,
          subscribe: gdprAcceptance,
          user_message: message,
          ...(siteInfo.isUK
            ? {
                what_are_you_looking_to_do:
                  submittedWhatAreYouLookingToDo,
              }
            : {}),
          preferred_days: submittedDays,
        },
        {
          email,
          full_name: `${firstName} ${lastName}`.trim(),
          phone,
        }
      )
    } else if (location?.id) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'contactFormSubmit',
          event_category: 'Contact Form Submissions',
          event_action: location.title,
          event_label: window.location.href,
        },
      })

      pushSegment(
        'Location Contact Form Submitted',
        {
          clinic_name: location.title,
          clinic_address: location.locationAddress,
          subscribe: gdprAcceptance,
          user_message: message,
          ...(siteInfo.isUK
            ? {
                what_are_you_looking_to_do:
                  submittedWhatAreYouLookingToDo,
              }
            : {}),
          preferred_days: submittedDays,
        },
        {
          email,
          full_name: `${firstName} ${lastName}`.trim(),
          phone,
        }
      )
    }
  }

  const handleAutopilot = async () => {
    const isTestEmail = getIsTestEmail(email)
    const isUsOrUk = Boolean(siteInfo.isUS || siteInfo.isUK)

    if (!gdprAcceptance || !isUsOrUk || isTestEmail) {
      return
    }

    const body = {
      contact: {
        Email: email,
        MailingPostalCode: postcode,
        MailingCountry: siteInfo.isUK ? 'United Kingdom' : siteInfo.name,
        custom: {
          'float--FaD--Search--Lat': appContext?.state.query.lat,
          'float--FaD--Search--Lng': appContext?.state.query.lng,
          'integer--FaD--Search--Radius': appContext?.state.query.radius,
          'string--FaD--Doctor--Contacted': appContext?.state.doctor?.title,
          ...(siteInfo.isUK && submittedWhatAreYouLookingToDo
            ? {
                'string--FaD--Intent':
                  submittedWhatAreYouLookingToDo,
              }
            : {}),
          ...(ENABLE_PREFERRED_DAYS && submittedDays.length
            ? { 'string--FaD--Preferred--Days': submittedDays.join(', ') }
            : {}),
        },
      },
    }

    fetch(
      process.env.NEXT_PUBLIC_DOCTORS_API_PROXY_URL +
        '/autopilot/captureUser.js',
      {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    ).catch(() => {})
  }

  const toggleDay = (value: string) => {
    if (value === 'any-day') {
      setDays((current) => (current.includes('any-day') ? [] : ['any-day']))
      return
    }

    setDays((current) => {
      const next = current.filter((item) => item !== 'any-day')

      if (next.includes(value)) {
        return next.filter((item) => item !== value)
      }

      return [...next, value]
    })
  }

  const validateForm = () => {
    if (!firstName || !lastName || !email || !phone || !postcode || !message) {
      setErrors({ message: ['Please complete all required fields.'] })
      return false
    }

    if (!gdprAcceptance) {
      setErrors({ message: ['Please accept the consent checkbox to continue.'] })
      return false
    }

    return true
  }

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    setErrors(undefined)
    setIsSubmitting(true)

    handleDataLayer()
    handleAutopilot()

    const res = await formie.send({
      doctor,
      location,
      firstName,
      lastName,
      email,
      phone,
      postcode,
      whatAreYouLookingToDo: submittedWhatAreYouLookingToDo,
      message,
      gdprAcceptance,
      days: submittedDays,
    })

    setIsSubmitting(false)

    if (!res.success) {
      setErrors(res.errors)
      return
    }

    setErrors(undefined)

    onSuccess({
      lastName,
      email,
      phone,
      message,
      isSubscribed: gdprAcceptance,
    })
  }

  return (
    <form onSubmit={submitForm}>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full md:w-1/2 px-3 mt-6">
          <AFormInput
            placeholder="First Name"
            required={true}
            value={firstName}
            version="standalone"
            onChangeHandler={setFirstName}
          />
        </div>

        <div className="w-full md:w-1/2 px-3 mt-6">
          <AFormInput
            placeholder="Last Name"
            required={true}
            value={lastName}
            version="standalone"
            onChangeHandler={setLastName}
          />
        </div>

        <div className="w-full md:w-1/2 px-3 mt-6">
          <AFormInput
            placeholder="Email"
            required={true}
            type="email"
            value={email}
            version="standalone"
            onChangeHandler={setEmail}
          />
        </div>

        <div className="w-full md:w-1/2 px-3 mt-6">
          <AFormInput
            placeholder="Cell Phone"
            required={true}
            value={phone}
            version="standalone"
            onChangeHandler={setPhone}
          />
        </div>

        <div className="w-full px-3 mt-6">
          <AFormInput
            placeholder="Postcode"
            required={true}
            value={postcode}
            version="standalone"
            onChangeHandler={setPostcode}
          />
        </div>

        {siteInfo.isUK && (
          <div className="w-full px-3 mt-6">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              What are you looking to do?
            </label>

            <AFormSelect
              name="whatAreYouLookingToDo"
              required={true}
              value={whatAreYouLookingToDo}
              version="standalone"
              options={LOOKING_TO_DO_OPTIONS}
              onChangeHandler={setWhatAreYouLookingToDo}
            />
          </div>
        )}

        <div className="w-full px-3 mt-6">
          <AFormTextarea
            placeholder="Message"
            required={true}
            value={message}
            version="standalone"
            onChangeHandler={setMessage}
          />
        </div>

        {ENABLE_PREFERRED_DAYS && (
          <div className="w-full px-3 mt-6">
            <div className="text-sm font-semibold text-gray-800 mb-2">Days</div>

            <div className="flex flex-col gap-3">
              {DAY_OPTIONS.map((day) => {
                const checked = days.includes(day.value)
                const DayIcon = checked ? SvgCheckTrueIcon : SvgCheckFalseIcon

                return (
                  <div
                    key={day.value}
                    className="cursor-pointer flex items-center"
                    onClick={() => toggleDay(day.value)}
                  >
                    <DayIcon className="flex-shrink-0 fill-current mr-3 text-primary-teal w-4 h-4" />
                    <span className="text-sm text-gray-800">{day.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div
        className="bg-gray-300 cursor-pointer flex mt-10 p-5"
        onClick={() => {
          setErrors(undefined)
          setGdprAcceptance(!gdprAcceptance)
        }}
      >
        <SvgCheckIcon className="flex-shrink-0 fill-current mr-4 text-primary-teal w-6 h-6" />
        <p className="text-xs text-gray-700 w-full">
          STAAR Surgical AG, as the data controller, would like to contact you
          periodically to provide information regarding our products, services,
          events and content that may be of interest to you. I would like to
          receive marketing or market research communications from STAAR Surgical
          via email. If you wish to stop receiving marketing or market research
          communications from STAAR Surgical you can unsubscribe via the link at
          the bottom of the relevant marketing email at any time.
        </p>
      </div>

      {errors?.message?.map((message: string, index: number) => (
        <div
          key={index}
          className="text-base font-semibold leading-7 text-gray-800 text-center lg:text-left text-secondary-red mt-4"
        >
          {message}
        </div>
      ))}

      <div className="flex flex-col lg:flex-row lg:gap-4 items-center mt-8">
        <AButton
          className={classNames('max-w-md w-full lg:w-auto', {
            'opacity-50 pointer-events-none': isSubmitting,
          })}
          color={siteInfo?.layout === CONST.SITE_LAYOUT.ICL ? 'teal' : 'pink'}
          size="lg"
          title={t('submit') ?? ''}
          type="submit"
        >
          <>{isSubmitting ? 'Submitting...' : t('submit')}</>
        </AButton>

        <AButton
          className="mt-4 lg:mt-0 max-w-md w-full lg:w-auto"
          color="black"
          size="lg"
          style="outline"
          title={t('cancel') ?? ''}
          type="button"
          onClick={onCancel}
        >
          <>{t('cancel')}</>
        </AButton>
      </div>
    </form>
  )
}
